/**
 * storefront-search.js
 *
 * Shopify's built-in storefront search drops `vendor`-field matches as soon as
 * the query has more than one word. In this store the author is stored in the
 * product `vendor` field, so searching a full author name ("עמוס עוז") returns
 * almost nothing — even though the Storefront API returns all of them.
 *
 * This module queries the Storefront API instead (which matches correctly) and
 * powers two surfaces:
 *   1) window.StorefrontSearch.query / queryAll / cardHTML — used by the live
 *      predictive dropdown (in theme.liquid).
 *   2) A results-page controller that replaces the native (broken) search grid
 *      with comprehensive results, in-stock first, with numbered pagination.
 *
 * Requires window.storefrontApiToken and window.Shopify.shop (set in theme.liquid).
 */

(function () {
  'use strict';

  const API_VERSION = '2024-10';
  const PRODUCT_QUERY = `
    query Search($q: String!, $first: Int!, $after: String, $sortKey: ProductSortKeys!) {
      products(first: $first, after: $after, query: $q, sortKey: $sortKey) {
        edges {
          cursor
          node {
            handle
            title
            vendor
            productType
            tags
            availableForSale
            featuredImage { url altText width height }
            priceRange { minVariantPrice { amount currencyCode } }
            compareAtPriceRange { minVariantPrice { amount } }
            badge: metafield(namespace: "custom", key: "badge") { value }
            variants(first: 2) { edges { node { id availableForSale } } }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }`;

  function config() {
    return {
      token: window.storefrontApiToken || '',
      shop: (window.Shopify && window.Shopify.shop) || window.location.host,
    };
  }

  async function rawQuery(term, opts) {
    opts = opts || {};
    const { token, shop } = config();
    if (!token) throw new Error('Storefront API token missing');
    const res = await fetch(`https://${shop}/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: PRODUCT_QUERY,
        variables: {
          q: term,
          first: opts.first || 24,
          after: opts.after || null,
          sortKey: opts.sortKey || 'RELEVANCE',
        },
      }),
    });
    const data = await res.json();
    if (data.errors) throw new Error(JSON.stringify(data.errors));
    return data.data.products;
  }

  function normalize(node) {
    const img = node.featuredImage;
    const price = node.priceRange.minVariantPrice;
    const cmp = node.compareAtPriceRange && node.compareAtPriceRange.minVariantPrice;
    const onSale = cmp && parseFloat(cmp.amount) > parseFloat(price.amount);
    const vEdges = (node.variants && node.variants.edges) || [];
    const firstVariant = vEdges[0] && vEdges[0].node;
    // Storefront variant id is a GID (gid://shopify/ProductVariant/123) — the
    // cart endpoint needs the trailing numeric id.
    const variantId = firstVariant ? (firstVariant.id.match(/\d+$/) || [''])[0] : '';
    return {
      handle: node.handle,
      url: '/products/' + node.handle,
      title: node.title,
      vendor: node.vendor || '',
      productType: node.productType || '',
      tags: node.tags || [],
      available: node.availableForSale,
      image: img ? img.url : '',
      imageAlt: (img && img.altText) || node.title,
      imageW: img ? img.width : 300,
      imageH: img ? img.height : 300,
      price: price.amount,
      currency: price.currencyCode,
      compareAt: onSale ? cmp.amount : null,
      badge: (node.badge && node.badge.value) || '',
      variantId: variantId,
      variantCount: vEdges.length,
    };
  }

  /** Remove characters that would break the Storefront query syntax. */
  function sanitizeTerm(term) {
    return (term || '')
      .replace(/["():*~]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function scopedFields(v) {
    return 'title:' + v + ' OR vendor:' + v + ' OR product_type:' + v + ' OR tag:' + v;
  }

  /**
   * Exact/whole-word scoped query (NO wildcard) over title/vendor/genre/tag.
   * Small and precise — guarantees the exact match is present and ranked first
   * (a wildcard query buries a 2-letter exact term like "אי" among all "אי…"
   * words). Multi-word terms are matched as an exact phrase.
   */
  function exactQuery(term) {
    const t = sanitizeTerm(term);
    if (!t) return '';
    return scopedFields(/\s/.test(t) ? '"' + t + '"' : t);
  }

  /**
   * Prefix scoped query: append `*` so partial input matches as a prefix.
   * Appending `*` to a multi-word term wildcards its LAST word, so this handles
   * both "בנו" → "בנות" and "עד שיו" → "עד שיום אחד".
   */
  function prefixQuery(term) {
    const t = sanitizeTerm(term);
    if (!t) return '';
    return scopedFields(t + '*');
  }

  /**
   * Broad (unscoped) query: a bare term searches ALL fields, INCLUDING the
   * product description — the only way to surface description matches (e.g. a
   * poet listed in an anthology's description; Shopify ignores a `body:` scope).
   * The trailing `*` makes the last word a prefix (works for single and
   * multi-word).
   */
  function broadQuery(term) {
    const t = sanitizeTerm(term);
    if (!t) return '';
    return t + '*';
  }

  /** Paginate a single query into a normalized list, up to `cap`. */
  async function paginateQuery(qstr, cap, batch, sortKey) {
    const all = [];
    if (!qstr) return all;
    let after = null;
    let hasMore = true;
    while (hasMore && all.length < cap) {
      const p = await rawQuery(qstr, { first: Math.min(batch, cap - all.length), after, sortKey });
      if (!p.edges.length) break;
      p.edges.forEach((e) => all.push(normalize(e.node)));
      hasMore = p.pageInfo.hasNextPage;
      after = p.pageInfo.endCursor;
    }
    return all;
  }

  /** Union two lists by product handle, keeping `primary` order first. */
  function mergeByHandle(primary, secondary) {
    const seen = {};
    const out = [];
    primary.concat(secondary).forEach((p) => {
      if (!seen[p.handle]) {
        seen[p.handle] = 1;
        out.push(p);
      }
    });
    return out;
  }

  /** Merge exact → prefix → description match sets, deduped by handle. */
  function mergeSets(exact, prefix, broad) {
    return mergeByHandle(mergeByHandle(exact, prefix), broad);
  }

  /** One "page" for the dropdown: exact + prefix + description matches, merged. */
  async function query(term, opts) {
    opts = opts || {};
    const first = opts.first || 24;
    const sortKey = opts.sortKey || 'RELEVANCE';
    const sets = await Promise.all([
      paginateQuery(exactQuery(term), first, first, sortKey),
      paginateQuery(prefixQuery(term), first, first, sortKey),
      paginateQuery(broadQuery(term), first, first, sortKey),
    ]);
    return { products: mergeSets(sets[0], sets[1], sets[2]), pageInfo: { hasNextPage: false } };
  }

  /**
   * Gather results for the results page:
   *   1) exact/whole-word scoped set (guarantees the exact match is present),
   *   2) prefix scoped set (partial-word matches, up to `cap`),
   *   3) a small bare set that adds description matches.
   * Merged by handle; `rankResults` then orders everything (in-stock first,
   * exactness next, description matches last).
   */
  async function queryAll(term, opts) {
    opts = opts || {};
    const cap = opts.cap || 250;
    const sortKey = opts.sortKey || 'RELEVANCE';
    const sets = await Promise.all([
      paginateQuery(exactQuery(term), 60, 50, sortKey),
      paginateQuery(prefixQuery(term), cap, 50, sortKey),
      paginateQuery(broadQuery(term), 50, 50, sortKey),
    ]);
    const merged = mergeSets(sets[0], sets[1], sets[2]);
    return { products: merged, hasMore: sets[1].length >= cap };
  }

  /** Normalize Hebrew text for matching (strip quotes/geresh, lowercase, trim). */
  function normText(s) {
    return (s || '')
      .replace(/["'׳״`´]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * How closely a product matches the term (higher = closer):
   *   5 exact title · 4 title starts-with · 3 whole word in title · 2 substring
   *   in title · 1 matched via author/genre/tag · 0 matched only via description.
   */
  function matchTier(product, term) {
    const q = normText(term);
    if (!q) return 0;
    const t = normText(product.title);
    if (t === q) return 5;
    if (t.indexOf(q) === 0) return 4;
    if (new RegExp('(^|\\s)' + escapeRegExp(q) + '(\\s|$)').test(t)) return 3;
    if (t.indexOf(q) !== -1) return 2;
    const hay = normText(
      (product.vendor || '') + ' ' + (product.productType || '') + ' ' + (product.tags || []).join(' ')
    );
    if (hay.indexOf(q) !== -1) return 1;
    return 0; // matched only via description (body)
  }

  /**
   * Re-order results: in-stock products first (sold-out always last), and within
   * each group most-exact match first, keeping Storefront relevance order (stable)
   * as the final tiebreaker.
   */
  function rankResults(products, term) {
    return (products || [])
      .map((p, i) => ({ p: p, i: i, tier: matchTier(p, term) }))
      .sort(function (a, b) {
        if (a.p.available !== b.p.available) return a.p.available ? -1 : 1;
        if (b.tier !== a.tier) return b.tier - a.tier;
        return a.i - b.i;
      })
      .map(function (x) {
        return x.p;
      });
  }

  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : s;
    return d.innerHTML;
  }

  function money(amount) {
    const n = parseFloat(amount);
    if (isNaN(n)) return '';
    return '₪' + (Number.isInteger(n) ? n.toString() : n.toFixed(2));
  }

  const SPINNER =
    '<div class="loading__spinner hidden"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';

  /** Append a Shopify CDN width param to an image URL. */
  function imgUrl(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  /** Faithful Dawn standard product card (matches snippets/card-product.liquid). */
  function cardHTML(p) {
    const title = escapeHtml(p.title);
    const url = escapeHtml(p.url);
    const scheme = window.__cardScheme || 'background-2';
    const placeholder = window.__searchPlaceholder || '';
    const sizes =
      '(min-width: 1300px) 292px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';

    // --- media (matches the <a><div.media><img></div></a> nesting Dawn uses) ---
    let media;
    if (p.image) {
      const srcset = [165, 360, 533, 720]
        .map((w) => escapeHtml(imgUrl(p.image, w)) + ' ' + w + 'w')
        .join(', ');
      media =
        '<div class="card__media"><a href="' + url + '"><div class="media media--transparent media--hover-effect">' +
        '<img srcset="' + srcset + '" src="' + escapeHtml(imgUrl(p.image, 533)) + '" sizes="' + sizes + '" ' +
        'alt="' + escapeHtml(p.imageAlt) + '" class="motion-reduce" loading="lazy" width="' + p.imageW + '" height="' + p.imageH + '">' +
        '</div></a></div>';
    } else {
      media = '<div class="card__media"><img src="' + escapeHtml(placeholder) + '" alt="' + title + '"></div>';
    }

    // --- badge (matches card-product.liquid: custom badge → sold out → on sale) ---
    let badge = '';
    if (p.badge) badge = escapeHtml(p.badge);
    else if (!p.available) badge = 'נמכר';
    else if (p.compareAt) badge = 'הנחה';
    const badgeHTML = badge
      ? '<div class="card__badge bottom left"><span class="badge badge--bottom-left color-inverse">' + badge + '</span></div>'
      : '';

    // --- price (hidden for sold-out products, matching card-product.liquid) ---
    const priceHTML = !p.available
      ? ''
      : '<div class="price' + (p.compareAt ? ' price--on-sale' : '') + '"><div class="price__container"><div class="price__regular">' +
        (p.compareAt
          ? '<span class="price-item price-item--regular"><s>' + money(p.compareAt) + '</s></span> '
          : '') +
        '<span class="price-item price-item--' + (p.compareAt ? 'sale' : 'regular') + '">' + money(p.price) + '</span>' +
        '</div></div></div>';

    // --- quick add (matches card-product.liquid: single-variant always shows a
    //     button — "הוספה לסל", or a disabled "נמכר" when sold out; multi-variant
    //     links to the product page to choose options) ---
    let quickAdd = '';
    if (p.variantCount <= 1 && p.variantId) {
      const dis = p.available ? '' : ' disabled';
      const label = p.available ? 'הוספה לסל' : 'נמכר';
      quickAdd =
        '<div class="quick-add no-js-hidden"><product-form data-section-id="search">' +
        '<form method="post" action="/cart/add" accept-charset="UTF-8" class="form" novalidate="novalidate" data-type="add-to-cart-form">' +
        '<input type="hidden" name="form_type" value="product"><input type="hidden" name="utf8" value="✓">' +
        '<input type="hidden" name="id" value="' + p.variantId + '" class="product-variant-id"' + dis + '>' +
        '<button type="submit" name="add" class="quick-add__submit button button--full-width button--secondary"' + dis + '>' +
        '<span>' + label + '</span>' + SPINNER + '</button>' +
        '</form></product-form></div>';
    } else if (p.variantCount > 1) {
      quickAdd =
        '<div class="quick-add no-js-hidden"><a href="' + url + '" class="quick-add__submit button button--full-width button--secondary" style="display:block;text-align:center;">בחירת אפשרויות</a></div>';
    }

    return (
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="card card--standard ' + (p.image ? 'card--media' : 'card--text') + '" style="--ratio-percent: 100%;">' +
      '<div class="card__inner color-' + scheme + ' gradient ratio" style="--ratio-percent: 100%;">' +
      media +
      badgeHTML +
      '</div>' +
      '<div class="card__content"><div class="card__information">' +
      '<h3 class="card__heading h2"><a href="' + url + '" class="full-unstyled-link">' + title + '</a></h3>' +
      '<div class="card-information">' +
      (p.vendor ? '<span class="caption-with-letter-spacing">' + escapeHtml(p.vendor) + '</span>' : '') +
      priceHTML +
      '</div></div></div>' +
      quickAdd +
      '</div></div>'
    );
  }

  window.StorefrontSearch = { query, queryAll, normalize, cardHTML, money, rankResults };

  /* ------------------------------------------------------------------ *
   * Results-page controller
   * ------------------------------------------------------------------ */

  class SearchResultsController {
    constructor(marker) {
      this.marker = marker;
      this.container = document.querySelector('#ProductGridContainer');
      this.perPage = parseInt(marker.dataset.perPage, 10) || 24;
      this.colsDesktop = marker.dataset.colsDesktop || '4';
      this.colsMobile = marker.dataset.colsMobile || '2';
      this.term = (new URLSearchParams(window.location.search).get('q') || '').trim();
      this.order = null;
      if (window.__cardScheme === undefined) window.__cardScheme = 'background-2';
      if (marker.dataset.placeholder) window.__searchPlaceholder = marker.dataset.placeholder;
      this.run();
      window.addEventListener('popstate', () => {
        if (this.order) this.render(this.currentPage());
      });
    }

    currentPage() {
      const p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      return p && p > 0 ? p : 1;
    }

    async run() {
      if (!this.term || !this.container) return;
      // Hide the native (server-rendered) results immediately and show a loader,
      // so native search's fuzzy result doesn't flash before our async query
      // resolves. Stash the native markup so we can restore it on API error.
      this.showLoading();
      try {
        const { products, hasMore } = await StorefrontSearch.queryAll(this.term, { cap: 250 });
        // The Storefront API is authoritative — take over the display in all
        // cases (even 0 results). Otherwise native search's fuzzy fallback shows
        // loosely-related products for typos/gibberish.
        this.hideNativeFilters();
        if (!products.length) {
          this.order = [];
          this.renderEmpty();
          return;
        }
        // Most-exact title match first; in-stock preferred within the same tier.
        this.order = StorefrontSearch.rankResults(products, this.term);
        this.hasMore = hasMore;
        this.render(this.currentPage());
        this.updateCount(products.length, hasMore);
      } catch (e) {
        // On a real API/network error, restore the native results.
        console.warn('[storefront-search] falling back to native results:', e);
        this.restoreNative();
      }
    }

    showLoading() {
      this.hideNativeFilters();
      if (this._nativeHTML === undefined) this._nativeHTML = this.container.innerHTML;
      this.container.innerHTML =
        '<div class="template-search__results collection page-width" style="text-align:center;padding:5rem 1rem;">' +
        '<div style="font-size:1.6rem;color:rgba(var(--color-foreground),0.6);">מחפש…</div></div>';
      this.container.classList.add('sfs-ready');
    }

    restoreNative() {
      if (this._nativeHTML !== undefined) this.container.innerHTML = this._nativeHTML;
      this.container.classList.add('sfs-ready');
    }

    renderEmpty() {
      this.container.innerHTML =
        '<div class="template-search__results collection page-width">' +
        '<div class="sfs-no-results" style="text-align:center;padding:5rem 1rem;font-size:1.6rem;">' +
        'לא נמצאו תוצאות עבור “' + escapeHtml(this.term) + '”' +
        '</div></div>';
      this.updateCount(0, false);
    }

    /** Find or build the grid <ul> inside #ProductGridContainer. */
    grid() {
      let ul = this.container.querySelector('ul.product-grid');
      if (ul) return ul;
      // Native rendered an empty / different structure — build the standard one.
      this.container.innerHTML =
        '<div class="template-search__results collection page-width">' +
        '<ul class="grid product-grid grid--' +
        this.colsMobile +
        '-col-tablet-down grid--' +
        this.colsDesktop +
        '-col-desktop" role="list"></ul>' +
        '<div class="pagination-wrapper"></div></div>';
      return this.container.querySelector('ul.product-grid');
    }

    render(page) {
      const ul = this.grid();
      const total = this.order.length;
      const pages = Math.max(1, Math.ceil(total / this.perPage));
      const current = Math.min(Math.max(1, page), pages);
      const start = (current - 1) * this.perPage;
      const slice = this.order.slice(start, start + this.perPage);

      ul.innerHTML = slice
        .map((p) => '<li class="grid__item">' + cardHTML(p) + '</li>')
        .join('');

      this.renderPagination(current, pages);
    }

    renderPagination(current, pages) {
      let wrapper = this.container.querySelector('.pagination-wrapper');
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'pagination-wrapper';
        (this.container.querySelector('.collection') || this.container).appendChild(wrapper);
      }
      if (pages <= 1) {
        wrapper.innerHTML = '';
        return;
      }
      // Links use href="#" so Dawn's pagination.liquid inline script (which does a
      // full-page reload for .pagination links carrying query params) ignores them
      // — our own capture handler paginates in place (no reload / flash).
      const caret =
        '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>';
      const parts = [];
      const left = Math.max(2, current - 1);
      const right = Math.min(pages - 1, current + 1);
      parts.push(1);
      if (left > 2) parts.push('…');
      for (let i = left; i <= right; i++) parts.push(i);
      if (right < pages - 1) parts.push('…');
      if (pages > 1) parts.push(pages);

      let html =
        '<nav class="pagination" role="navigation" aria-label="עמודים"><ul class="pagination__list list-unstyled" role="list">';
      if (current > 1)
        html +=
          '<li><a href="#" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-sfs-page="' + (current - 1) + '" aria-label="עמוד קודם">' + caret + '</a></li>';
      parts.forEach((part) => {
        if (part === '…') html += '<li><span class="pagination__item">…</span></li>';
        else if (part === current)
          html += '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="עמוד ' + part + '">' + part + '</a></li>';
        else
          html += '<li><a href="#" class="pagination__item link" data-sfs-page="' + part + '" aria-label="עמוד ' + part + '">' + part + '</a></li>';
      });
      if (current < pages)
        html +=
          '<li><a href="#" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-sfs-page="' + (current + 1) + '" aria-label="עמוד הבא">' + caret + '</a></li>';
      html += '</ul></nav>';
      wrapper.innerHTML = html;
    }

    updateCount(count, hasMore) {
      const text =
        count === 0
          ? 'לא נמצאו תוצאות עבור "' + this.term + '"'
          : count + (hasMore ? '+' : '') + ' תוצאות עבור "' + this.term + '"';
      // Replace the native "no results" line / status line if present.
      let status = document.querySelector('.template-search__header p[role="status"]');
      if (!status) {
        const header = document.querySelector('.template-search__header');
        if (header) {
          status = document.createElement('p');
          status.setAttribute('role', 'status');
          header.appendChild(status);
        }
      }
      if (status) status.textContent = text;
    }

    hideNativeFilters() {
      // Native facets are built from the (empty) native results, so they can't
      // filter Storefront results — hide them to avoid a broken sidebar.
      const filters = document.querySelector('#main-search-filters');
      if (filters) filters.style.display = 'none';
      const sort = document.querySelector('.facets-vertical-sort');
      if (sort) sort.style.display = 'none';
    }
  }

  // Reload-free pagination for the results grid.
  document.addEventListener(
    'click',
    function (e) {
      const link = e.target.closest('a[data-sfs-page]');
      if (!link || !window.__sfsController || !window.__sfsController.order) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const page = parseInt(link.dataset.sfsPage, 10) || 1;
      const params = new URLSearchParams(window.location.search);
      params.set('page', String(page));
      history.pushState({}, '', window.location.pathname + '?' + params.toString());
      window.__sfsController.render(page);
      const top = document.querySelector('#ProductGridContainer');
      if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    true
  );

  function initResults() {
    const marker = document.querySelector('[data-storefront-search]');
    if (!marker || window.__sfsController) return;
    window.__sfsController = new SearchResultsController(marker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initResults);
  } else {
    initResults();
  }
})();
